import React, { memo, useState } from 'react';
import { List, Map } from 'immutable';
import classNames from 'classnames';

import { unwrap } from '../../../utils/ChangeSpy';
import { ResizablePopup } from '../../helpers/Popup';
import { Spinner } from '../../helpers/Spinner';
import Button from '../../helpers/Button';
import FormGroup from '../../helpers/FormGroup';
import BaseInput from '../../helpers/Input';
import BaseTextarea from '../../helpers/Textarea';
import BaseWysiwyg from '../../helpers/Wysiwyg/Wysiwyg';
import BaseCheckbox from '../../helpers/Checkbox';
import BaseSelect from '../../helpers/Select';
import BaseActivityDropdown from '../../helpers/ActivityDropdown';
import { IconButton } from '../../helpers/IconButton';
import PrettyError from '../../decorators/PrettyError';
import DisplayError from '../../decorators/DisplayError';
import MeetingDetailsNotePopup from '../MeetingDetailsNotePopup';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));
const Textarea = PrettyError(DisplayError(BaseTextarea));
const Wysiwyg = PrettyError(DisplayError(BaseWysiwyg));
const Checkbox = PrettyError(DisplayError(BaseCheckbox));
const Select = PrettyError(DisplayError(BaseSelect));
const ActivityDropdown = PrettyError(DisplayError(BaseActivityDropdown));

const defaultActivities = List();
const defaultActivity = Map();

const Tab = {
  TARGET: 'target',
  BUYER: 'buyer',
};

const MeetingDetailsPopupComponent = ({
  activities = defaultActivities,
  companyType = Tab.TARGET,
  meeting,
  suggestAnalyst,
  suggestDealmaker,
  suggestCompletedBy,
  suggestTeam,
  suggestTarget,
  suggestBuyer,
  isLoading,
  isLoadingCompletedEvents,
  isDisabledAction,
  types,
  onChange,
  onSave,
  onClose,
  onActivityChange,
  onDownload,
  onChangeTab,
}) => {
  const [tab, setTab] = useState(companyType);
  const [isOpenMeetingDetailsNotePopup, setMeetingDetailsNotePopup] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const hasSummaryHtml = unwrap(meeting.get('summaryHtml')) !== '';
  const completedEventLabelClass = classNames('font-normal', {
    'disabled-link': isDisabledAction,
  });
  const callTranscriptClass = classNames('text-right', {
    'col-sm-6': hasSummaryHtml,
    'col-sm-12': !hasSummaryHtml,
  });

  const targetActivities = activities?.get('target', defaultActivities);
  const buyerActivities = activities?.get('buyer', defaultActivities);

  const handleTabChange = (event, activeTab) => {
    event.preventDefault();
    setTab(activeTab);
    onChangeTab(activeTab);
  };

  const footer = (
    <>
      <Button
        className="btn-primary"
        disabled={!meeting.get('isValid', false)}
        loading={isLoading}
        onClick={onSave}
        type="button"
      >
        Save
      </Button>
      <Button key="close" data-dismiss="modal" onClick={onClose}>
        Close
      </Button>
    </>
  );

  const existingCompetedAction = types && unwrap(meeting.get('type', null)) === 'update_completed_action' && (
    <div>
      <Select
        className="input-sm"
        defaultText="Select a completed event"
        label="Event"
        name="eventId"
        nameKey="text"
        onChange={onChange}
        options={types}
        root={meeting}
        valueKey="id"
        deep
        showLabel
      />
    </div>
  );

  const handleOpenMeetingDetailsNote = () => {
    setMeetingDetailsNotePopup(true);
  };

  const handleCloseMeetingDetailsNote = () => {
    setMeetingDetailsNotePopup(false);
  };

  const handleParticipantsToggle = () => {
    setIsParticipantsOpen(!isParticipantsOpen);
  };

  return (
    <>
      <MeetingDetailsNotePopup
        html={unwrap(meeting.get('summaryHtml'))}
        isOpen={isOpenMeetingDetailsNotePopup}
        onClose={handleCloseMeetingDetailsNote}
      />
      <ResizablePopup
        footer={footer}
        header="Meeting Details"
        height={800}
        id="meetingDetailsPopup"
        minConstraints={[600, 600]}
        width={670}
      >
        <div className="flexWrapper h100p">
          <div className="pb10 cursor-pointer" onClick={handleParticipantsToggle}>
            <div className="truncate-text">
              <b>Participants: </b>
              <i aria-hidden="true" className={isParticipantsOpen ? 'fa fa-caret-up' : 'fa fa-caret-down'} />
              {!isParticipantsOpen && <span className="pl10">{meeting.get('participantsText')}</span>}
            </div>
          </div>
          <div className={classNames('panel-collapse collapse', { in: isParticipantsOpen })} id="participants-slide">
            <div className={style.participants}>{meeting.get('participantsText')}</div>
          </div>
          <ul className="nav nav-tabs mb10">
            <li>
              <a className={tab === Tab.TARGET && 'active'} onClick={event => handleTabChange(event, Tab.TARGET)}>
                Target
              </a>
            </li>
            <li>
              <a className={tab === Tab.BUYER && 'active'} onClick={event => handleTabChange(event, Tab.BUYER)}>
                Buyer
              </a>
            </li>
          </ul>
          <div className={classNames(style.tabContent, 'tab-content flexWrapper')}>
            {tab === Tab.TARGET && (
              <div className="tab-pane active flexWrapper h100p" role="tabpanel">
                <div className="row">
                  <FormGroup className="col-xs-3">
                    <Input
                      className="input-sm"
                      label="Date"
                      name="date"
                      onChange={onChange}
                      placeholder="Date"
                      root={meeting}
                      tabIndex={1}
                      type="date"
                      deep
                      required
                      showLabel
                    />
                  </FormGroup>
                  <div className="col-xs-3">{suggestTeam}</div>
                  <FormGroup className="col-xs-3">
                    <Input
                      className="input-sm"
                      label={
                        <>
                          Buyer&nbsp;
                          {meeting.getIn(['buyerId', 'value']) && (
                            <IconButton
                              href={`/company/${meeting.getIn(['buyerId', 'value'])}`}
                              icon="reply"
                              size="sm"
                              title="Open company in a new tab"
                            />
                          )}
                        </>
                      }
                      name="buyerName"
                      placeholder="Buyer"
                      root={meeting}
                      deep
                      readOnly
                      showLabel
                    />
                  </FormGroup>
                  <div className="col-xs-3">{suggestTarget}</div>
                </div>
                <div className="row">
                  <div className="col-xs-3">{suggestAnalyst}</div>
                  <div className="col-xs-3">{suggestDealmaker}</div>
                  <FormGroup className="col-xs-3">
                    <Input
                      className="input-sm"
                      label={
                        <>
                          Project&nbsp;
                          {meeting.getIn(['projectId', 'value']) && (
                            <IconButton
                              href={`/project/${meeting.getIn(['projectId', 'value'])}`}
                              icon="reply"
                              size="sm"
                              title="Open project in a new tab"
                            />
                          )}
                        </>
                      }
                      name="projectName"
                      placeholder="Project"
                      root={meeting}
                      deep
                      readOnly
                      showLabel
                    />
                  </FormGroup>
                  <FormGroup className="col-xs-3">
                    <Select
                      className="input-sm"
                      defaultText="Select a Contact"
                      label={
                        <>
                          Contact&nbsp;
                          {meeting.getIn(['contactId', 'value']) !== 0 && (
                            <IconButton
                              href={`/contact/${meeting.getIn(['contactId', 'value'])}`}
                              icon="reply"
                              size="sm"
                              title="Open contact in a new tab"
                            />
                          )}
                        </>
                      }
                      name="contactId"
                      nameKey="fullName"
                      onChange={onChange}
                      options={meeting.get('contacts')}
                      placeholder="Contact"
                      root={meeting}
                      valueKey="id"
                      deep
                      disableDefault
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className={classNames(style.fieldRow, 'row')}>
                  <FormGroup className="col-xs-3">
                    <ActivityDropdown
                      activities={targetActivities}
                      info={meeting.get('activityMap', defaultActivity)}
                      name="activity"
                      onChange={onActivityChange}
                      tabIndex="4"
                      isSinglePanel
                    />
                  </FormGroup>
                  <div className="col-xs-3">{suggestCompletedBy}</div>
                  <FormGroup className={classNames(style.controlCell, 'col-xs-3')}>
                    <Checkbox
                      deep="checked"
                      label="Completed"
                      name="completed"
                      root={meeting}
                      tabIndex="2"
                      defaultChecked
                      disabled
                    />
                  </FormGroup>
                </div>
                <div className="row">
                  <FormGroup className="col-sm-12">
                    <Textarea
                      label="Description"
                      name="title"
                      onChange={onChange}
                      placeholder="Description"
                      root={meeting}
                      rows="2"
                      tabIndex="5"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="flexItem all-h100p mb5">
                  <div className="h100p row">
                    <FormGroup className={classNames(style.wysiwygContainer, 'col-sm-12')}>
                      <Wysiwyg
                        label="Deal Notes"
                        name="dealNotes"
                        onChange={onChange}
                        placeholder="Deal Notes"
                        root={meeting}
                        tabIndex={6}
                        deep
                        showLabel
                      />
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <fieldset disabled={isDisabledAction}>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="completeNextAction">
                            Overwrite &quot;Next Action&quot;&nbsp;
                            <span>
                              <i
                                aria-hidden="true"
                                className="fa fa-exclamation-circle c-orange cursor-default"
                                title="This will overwrite whatever Next Action currently exists, and prompt the user to create a new Next Action as a next step."
                              />
                            </span>
                          </label>
                          <input
                            id="completeNextAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="complete_next_action"
                          />
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="createCompletedAction">
                            Create new completed &quot;Action&quot;&nbsp;
                            <i
                              aria-hidden="true"
                              className="fa fa-exclamation-circle c-orange cursor-default"
                              title="This simply creates a completed ‘Action’ without disturbing the existing ‘Next Action’."
                            />
                          </label>
                          <input
                            id="createCompletedAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="create_completed_action"
                          />
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="updateCompletedAction">
                            Modify existing completed &quot;Action&quot;&nbsp;
                            <i
                              aria-hidden="true"
                              className="fa fa-exclamation-circle c-orange cursor-default"
                              title="This allows you to overwrite a pre-existing completed ‘Action’ with this new data."
                            />
                          </label>
                          <input
                            id="updateCompletedAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="update_completed_action"
                          />
                        </div>
                        {isLoadingCompletedEvents ? (
                          <div className="p-relative mt16">
                            <Spinner className="bg-white" size="small" />
                          </div>
                        ) : (
                          existingCompetedAction
                        )}
                      </FormGroup>
                    </fieldset>
                  </div>
                </div>
              </div>
            )}
            {tab === Tab.BUYER && (
              <div className="tab-pane active flexWrapper h100p" role="tabpanel">
                <div className="row">
                  <FormGroup className="col-xs-3">
                    <Input
                      className="input-sm"
                      label="Date"
                      name="date"
                      onChange={onChange}
                      placeholder="Date"
                      root={meeting}
                      tabIndex={1}
                      type="date"
                      deep
                      required
                      showLabel
                    />
                  </FormGroup>
                  <div className="col-xs-3">{suggestCompletedBy}</div>
                  <div className="col-xs-3">{suggestBuyer}</div>
                  <FormGroup className="col-xs-3">
                    <Select
                      className="input-sm"
                      defaultText="Select a Contact"
                      label={
                        <>
                          Contact&nbsp;
                          {meeting.getIn(['contactId', 'value']) !== 0 && (
                            <IconButton
                              href={`/contact/${meeting.getIn(['contactId', 'value'])}`}
                              icon="reply"
                              size="sm"
                              title="Open contact in a new tab"
                            />
                          )}
                        </>
                      }
                      name="contactId"
                      nameKey="fullName"
                      onChange={onChange}
                      options={meeting.get('contacts')}
                      placeholder="Contact"
                      root={meeting}
                      valueKey="id"
                      deep
                      disableDefault
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className={classNames(style.fieldRow, 'row')}>
                  <FormGroup className="col-xs-3">
                    <ActivityDropdown
                      activities={buyerActivities}
                      info={meeting.get('activityMap', defaultActivity)}
                      name="activity"
                      onChange={onActivityChange}
                      tabIndex="4"
                      isSinglePanel
                    />
                  </FormGroup>
                  <FormGroup className={classNames(style.controlCell, 'col-xs-3')}>
                    <Checkbox
                      deep="checked"
                      label="Completed"
                      name="completed"
                      root={meeting}
                      tabIndex="2"
                      defaultChecked
                      disabled
                    />
                  </FormGroup>
                </div>
                <div className="row">
                  <FormGroup className="col-sm-12">
                    <Textarea
                      label="Description"
                      name="title"
                      onChange={onChange}
                      placeholder="Description"
                      root={meeting}
                      rows="2"
                      tabIndex="5"
                      deep
                      showLabel
                    />
                  </FormGroup>
                </div>
                <div className="flexItem all-h100p mb5">
                  <div className="h100p row">
                    <FormGroup className={classNames(style.wysiwygContainer, 'col-sm-12')}>
                      <Wysiwyg
                        label="Marketing/Approach"
                        name="dealNotes"
                        onChange={onChange}
                        placeholder="Marketing/Approach"
                        root={meeting}
                        tabIndex={6}
                        deep
                        showLabel
                      />
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <fieldset disabled={isDisabledAction}>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="completeNextAction">
                            Overwrite &quot;Next Action&quot;&nbsp;
                            <span>
                              <i
                                aria-hidden="true"
                                className="fa fa-exclamation-circle c-orange cursor-default"
                                title="This will overwrite whatever Next Action currently exists, and prompt the user to create a new Next Action as a next step."
                              />
                            </span>
                          </label>
                          <input
                            id="completeNextAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="complete_next_action"
                          />
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="createCompletedAction">
                            Create new completed &quot;Action&quot;&nbsp;
                            <i
                              aria-hidden="true"
                              className="fa fa-exclamation-circle c-orange cursor-default"
                              title="This simply creates a completed ‘Action’ without disturbing the existing ‘Next Action’."
                            />
                          </label>
                          <input
                            id="createCompletedAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="create_completed_action"
                          />
                        </div>
                      </FormGroup>
                      <FormGroup>
                        <div className="radio-wrap radio-wrap--reverse">
                          <label className={completedEventLabelClass} htmlFor="updateCompletedAction">
                            Modify existing completed &quot;Action&quot;&nbsp;
                            <i
                              aria-hidden="true"
                              className="fa fa-exclamation-circle c-orange cursor-default"
                              title="This allows you to overwrite a pre-existing completed ‘Action’ with this new data."
                            />
                          </label>
                          <input
                            id="updateCompletedAction"
                            name="type"
                            onChange={onChange}
                            type="radio"
                            value="update_completed_action"
                          />
                        </div>
                        {isLoadingCompletedEvents ? (
                          <div className="p-relative mt16">
                            <Spinner className="bg-white" size="small" />
                          </div>
                        ) : (
                          existingCompetedAction
                        )}
                      </FormGroup>
                    </fieldset>
                  </div>
                </div>
              </div>
            )}
            <div className="row">
              {hasSummaryHtml && (
                <div className="col-sm-6 text-left">
                  <Button className="btn-link" onClick={handleOpenMeetingDetailsNote}>
                    Detailed Notes
                  </Button>
                </div>
              )}
              <div className={callTranscriptClass}>
                <Button className="btn-link" onClick={onDownload}>
                  Call Transcript
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ResizablePopup>
    </>
  );
};

export const MeetingDetailsPopup = memo(MeetingDetailsPopupComponent);
